<template>
  <div class="product-variants">
    <table>
      <colgroup>
        <col v-for="n in 1" :key="n" :class="'table-col-' + n++" />
      </colgroup>
      <thead>
        <tr>
          <th v-if="headerVisibleStatus" />
          <th>Artikelcode</th>
          <th>Omschrijving</th>
          <th>Verpakking</th>
          <th v-if="isCustomerFlow || isBundle" class="product-variants__center">Vergoed</th>
          <th v-if="isBundle" class="product-variants__center">Aantal*</th>
          <th v-if="!isReplenishmentFlow" class="product-variants__center">Favoriet</th>
          <th v-if="!isBundle && !isAssortmentFlow" />
        </tr>
      </thead>
      <tbody>
        <tr v-for="(productVariant, index) in productVariants" :key="index">
          <td v-if="productVariant.imageUrl !== ''">
            <div class="product-variants__imgWrapper">
              <img :src="productVariant.imageUrl" @error="handleImageError" />
            </div>
          </td>
          <td :class="{ clickable: isBundle }" @click="goToBaseProductDetailsPage(productVariant.baseProductId)">
            {{ productVariant.productCode }}
          </td>
          <td>{{ $toTitleCase(productVariant.title)}}</td>
          <td>{{ productVariant.packagingUnit }} {{ productVariant.packagingLabel }}</td>
          <td v-if="isCustomerFlow || isBundle" class="product-variants__center">
            <v-clipboard-alert-icon
              v-if="!productVariant.isInsured"
              class="not-insured"
              title="Niet vergoed"
            /><v-clipboard-check-icon v-if="productVariant.isInsured" class="is-insured" title="Vergoed" />
          </td>
          <td v-if="isBundle" class="product-variants__center">
            {{ productVariant.amount }}
          </td>
          <td
            v-if="!isReplenishmentFlow"
            id="favoriteIcon"
            class="product-variants__center"
            @click="favorite(productVariant, baseProduct)"
          >
            <component
              :is="getEnumValue(favoriteStatusEnum, String(productVariant.isFavourite))"
              class="favorite-icon"
            />
          </td>
          <td v-if="favoriteSelected && !isBundle && !productVariant.orderable && !isAssortmentFlow
                    && productVariant.productRelationType === 'Followup'
                    && productVariant.alternativeProductModel !== null">
            <v-button md cta class="product-variants__order" @click.prevent="showAlternateProducts(productVariant.alternativeProductModel, 1)">
              Bekijk opvolgend product
            </v-button>
          </td>
          <td v-else-if="favoriteSelected && !isBundle && !productVariant.orderable && !isAssortmentFlow
                    && productVariant.productRelationType === 'Substitute'
                    && productVariant.alternativeProductModel !== null">
            <v-button md cta class="product-variants__order" @click.prevent="showAlternateProducts(productVariant.alternativeProductModel, 2)">
              Bekijk vervangend product
            </v-button>
          </td>
          <td v-else-if="favoriteSelected && !isBundle && !productVariant.orderable && !isAssortmentFlow
                    && productVariant.productRelationType === 'Alternatives'
                    && productVariant.alternativeProductModel !== null">
            <v-button md cta class="product-variants__order" @click.prevent="showAlternateProducts(productVariant.alternativeProductModel, 3)">
              Bekijk alternatieve product
            </v-button>
          </td>
          <td v-else-if="!isBundle && !productVariant.orderable && !isAssortmentFlow
                    && productVariant.productRelationType === 'Followup' && productVariant.isAlternativesBPIdAvailable">
            <v-button md cta class="product-variants__order" @click.prevent="goToAlternativesProductDetailsPage(productVariant.alternativesBaseProductId)">
              Bekijk opvolgend product
            </v-button>
          </td>
          <td v-else-if="!isBundle && !productVariant.orderable && !isAssortmentFlow
                    && productVariant.productRelationType === 'Substitute' && productVariant.isAlternativesBPIdAvailable">
            <v-button md cta class="product-variants__order" @click.prevent="goToAlternativesProductDetailsPage(productVariant.alternativesBaseProductId)">
              Bekijk vervangend product
            </v-button>
          </td>
          <td v-else-if="!isBundle && !productVariant.orderable && !isAssortmentFlow
                    && productVariant.productRelationType === 'Alternatives' && productVariant.isAlternativesBPIdAvailable">
            <v-button md cta class="product-variants__order" @click.prevent="goToAlternativesProductDetailsPage(productVariant.alternativesBaseProductId)">
              Bekijk alternatieve product
            </v-button>
          </td>
          <td v-else-if="!isBundle && productVariant.orderable && !isAssortmentFlow">
            <v-button md cta class="product-variants__order" @click.prevent="order(productVariant.productId, count)">
              Bestellen
            </v-button>
          </td>
          <td v-else-if="!isBundle && String(productVariant.MaterialStatusCode) === '30' && !isAssortmentFlow">Op bestelling verkrijgbaar</td>
          <td v-else-if="!isBundle && !productVariant.orderable && !isAssortmentFlow">Niet bestelbaar</td>
        </tr>
      </tbody>
    </table>
    <v-overlay v-if="showProductFavourites">
      <v-product-favourites
        :base-product="selectedBaseProductForFavorite"
        :product="selectedProductForFavorite"
        :product-id="productId"
        @success="successSaveFavorite(...arguments)"
        @cancel="closeOverlay"
      />
    </v-overlay>
    <!--Pop up to show alternative products -->
    <generic-modal v-if="showAlternateProductPopUp" :has-close-button="true" :modal-Medium-Width="true" @close="closeAlternateProductModal">
      <template #header>
        <div v-if="productRelationType == 1" class="mb-4">Opvolgend product</div>
        <div v-if="productRelationType == 2" class="mb-4">Vervangend product</div>
        <div v-else-if="productRelationType == 3" class="mb-4">Alternatief product</div>
      </template>
      <template #body>
        <div class="mb-4">
          <Alert
            :html="true"
            :htmlMessage="alternativeProductHtmlMsg"
            type="info"
          ></Alert>
        </div>
        <div class="relative mb-4">
          <div class="grid sm:grid-rows-4 md:grid-cols-2 lg:grid-cols-2 gap-2 mb-4">
            <card
              v-for="item in this.alternateCards"
              :key="item.id"
              class="border border-disabled-background h-full flex flex-col"
              :class="[item.isOriginalProduct ? 'bg-primary-product' : '']"
            >
              <template v-slot>
                <div class="flex flex-col flex-grow -mt-6">
                  <div class="flex items-center justify-center my-2 h-28 w-full flex-shrink-0">
                    <img
                      :src="getImageUrl(item.imageUrl)"
                      @error="handleImageError"
                      class="mx-auto max-h-full max-w-full"
                    />
                  </div>
                  <div
                    class="text-tdefault-color font-semibold text-sm mb-1 cursor-pointer h-11 overflow-hidden"
                  >
                    {{ $toTitleCase(item.name) }}
                  </div>
                  <div class="text-talternative-color text-sm mb-4">{{ item.packagingDetails }}</div>
                </div>
                <div class="mt-auto">
                  <div class="grid grid-cols-2 gap-2 mb-2">
                    <div class="flex items-center text-sm">
                      <span
                        class="inline-flex items-center gap-1 border-b border-dashed px-1"
                        :class="
                          item.orderable
                            ? ''
                            : 'text-warning-default border-warning-default'
                        "
                      >
                        <component :is="item.orderable ? '' : 'XCircleIcon'" class="h-5 w-5" />
                        {{ item.orderable ? '' : 'Niet leverbaar' }}
                      </span>
                    </div>
                    <div class="flex items-center text-sm">
                      <span
                        class="inline-flex items-center gap-1 border-b border-dashed px-1"
                        :class="
                          item.insured
                            ? 'text-success-default border-success-default'
                            : 'text-danger-text border-danger-text'
                        "
                      >
                        <component :is="item.insured ? 'CheckCircleIcon' : 'ExclamationCircleIcon'" class="h-5 w-5" />
                        {{ item.insured ? 'Vergoed' : 'Niet vergoed' }}
                      </span>
                    </div>
                  </div>

                  <div class="-mb-10" v-if="!item.isOriginalProduct && item.orderable">
                    <table>
                      <tbody>
                        <tr>
                          <td class="align-middle pl-10">
                            <button
                              class="flex justify-center pl-5 pr-5 text-white text-xs md:text-base font-medium items-center bg-primary-default border border-primary-default rounded-full p-2"
                              @click.prevent="switchFavoriet(item.productId, item.originalProductId, item.baseProductId)"
                            >
                              Vervang favoriet
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>
            </card>
          </div>
          <div class="absolute w-full top-48 z-50" v-if="recentAlternateProducts.length != 0 && alternateCards.length > 0">
            <div
              class="flex mb-4"
              :class="[(showAlternateNext && showAlternatePrev) || (showAlternatePrev && !showAlternateNext) ? 'justify-between items-center' : 'justify-end']"
            >
              <button
                v-if="showAlternatePrev"
                @click="alternatePrev"
                :class="{ hidden: alternateSlideIndex === 0 }"
                class="rounded-full bg-primary-default border border-primary-default -ml-4"
              >
                <ChevronLeftIcon class="h-8 w-8 inline-block text-white" aria-hidden="true" />
              </button>
              <button
                v-if="showAlternateNext"
                @click="alternateNext"
                :class="{ hidden: alternateSlideIndex + alternatesPerPage >= recentAlternateProducts.length }"
                class="rounded-full bg-primary-default border border-primary-default -mr-4"
              >
                <ChevronRightIcon class="h-8 w-3 w-8 inline-block text-white" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      </template>
      <template #footer>
      </template>
    </generic-modal>
  </div>
</template>

<script>
import axios from 'axios';
import { computed } from 'vue';
import VClipboardCheckIcon from 'icons/ClipboardCheck';
import VClipboardAlertIcon from 'icons/ClipboardAlert';
import VHeartIcon from 'icons/Heart';
import VHeartOutlineIcon from 'icons/HeartOutline';
import VProductFavourites from './ProductFavourites';
import router from '@/router';
import GenericModal from '@/components/shared/GenericModal';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/vue/20/solid';
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from '@heroicons/vue/24/outline';

import {
  getBaseProductDetails
} from '@/services';

export default {
  name: 'ProductVariants',
  components: {
    VHeartIcon,
    VHeartOutlineIcon,
    VProductFavourites,
    VClipboardCheckIcon,
    VClipboardAlertIcon,
    GenericModal,
    ChevronRightIcon,
    ChevronLeftIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    XCircleIcon
  },
  props: {
    productVariants: Array,
    isBundle: {
      type: Boolean,
      default: false
    },
    baseProduct: {
      type: Object
    },
    favoriteSelected: {
      type: Boolean,
      default: false
    },
    favoriteBundleIds: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      deliveryStatusClassEnum: {
        0: 'green',
        1: 'yellow',
        2: 'red'
      },
      favoriteStatusEnum: {
        false: 'v-heart-outline-icon',
        true: 'v-heart-icon'
      },
      count: 1,
      showProductFavourites: false,
      productId: null,
      bpId: '',
      selectedProductForFavorite: null,
      selectedBaseProductForFavorite: null,
      showAlternateProductPopUp: false,
      productRelationType: 0,
      alternativeProductHtmlMsg: '<b>Onze product specialisten raden dit alternatieve product aan </b> </br> Dit product is niet te bestellen. Onze product specialisten hebben voor u een alternatief product geselecteerd.',
      recentAlternateProducts: null,
      alternatePages: 0,
      alternatesPerPage: 2,
      alternateCards: null,
      alternateSlideIndex: 0,
      showAlternatePrev: false,
      showAlternateNext: false,
    };
  },
  computed: {
    headerVisibleStatus() {
      let status = false;
      for (let i = 0; i < this.productVariants.length; i++) {
        if (this.productVariants[i].imageUrl !== '') {
          status = true;
          break;
        }
      }
      return status;
    },
    isReplenishmentFlow() {
      return router.currentRoute.value.meta.flow === 'replenishment';
    },
    isAssortmentFlow() {
      return router.currentRoute.value.meta.flow === 'assortment';
    },
    isCustomerFlow() {
      return router.currentRoute.value.meta.flow === 'customer';
    }
  },
  methods: {
    /**
     * @function getEnumValue
     * @param {object} - enum list
     * @param {string} - enum key
     * @returns {any} - value that matches the key
     */
    getEnumValue(enumList, enumKey) {
      if (enumKey === 'null' || enumKey === '') {
        return enumList.false;
      }
      for (const key in enumList) {
        if (enumList.hasOwnProperty(key)) {
          if (key === enumKey) {
            return enumList[key];
          }
        }
      }
    },
    favorite(product, baseProduct) {
      const productId = product.productId;

      this.selectedProductForFavorite = product;
      this.selectedBaseProductForFavorite = baseProduct;
      this.productId = productId;
      this.showProductFavourites = true;
    },
    switchFavoriet(newProductId, oldProductId, baseProductId){
      axios
        .post('FavouriteProduct/SwitchProductInFavouriteList', {
          bundleIds: this.favoriteBundleIds,
          newProductId: newProductId,
          oldProductId: oldProductId
        })
        .then((response) => {
          if (response && response.data) {
            this.closeAlternateProductModal();
            this.$emit('switchFavouriteSuccess');
          }
        });
    },
    order(productId, count) {
      this.$emit('order', { productId, count });
    },
    successSaveFavorite(isAdded) {
      this.selectedProductForFavorite = null;
      this.selectedBaseProductForFavorite = null;
      this.productId = null;
      this.closeOverlay();
      this.$emit('favouriteSuccess');
    },
    closeOverlay() {
      this.showProductFavourites = false;
    },
    goToBaseProductDetailsPage(baseProductId) {
      if (baseProductId && this.isBundle) {
        router.push('/catalog/baseProduct/' + baseProductId);
      }
    },
    goToAlternativesProductDetailsPage(baseProductId) {
      if (baseProductId) {
        router.push('/catalog/baseProduct/' + baseProductId);
      }
    },
    showAlternateProducts(alternativeProductModel, type) {
      this.productRelationType = type;
      if(type === 1){
        this.alternativeProductHtmlMsg = this.alternativeProductHtmlMsg.replaceAll("alternatieve", "opvolgende");
        this.alternativeProductHtmlMsg = this.alternativeProductHtmlMsg.replaceAll("alternatief", "opvolgend");
      }
      else if(type === 2)
      {
        this.alternativeProductHtmlMsg = this.alternativeProductHtmlMsg.replaceAll("alternatieve", "vervangende");
        this.alternativeProductHtmlMsg = this.alternativeProductHtmlMsg.replaceAll("alternatief", "vervangend");
      }

      this.recentAlternateProducts = computed(() => {
        const computedAlternateProducts = Array.isArray(alternativeProductModel) ? alternativeProductModel : [];
        const firstProduct = computedAlternateProducts[0]?.ProductId;

        const result = computedAlternateProducts.map((item) => ({
          ...item,
          showLabel: item.ProductId === firstProduct
        }));
        // Slice if filterCount is present
        return result;
      });

      this.alternatePages = computed(() => {
        return Math.ceil(this.recentAlternateProducts.length / this.alternatesPerPage);
      });

      this.alternateCards = computed(() => {
        let start = this.alternateSlideIndex * this.alternatesPerPage;
        let end = start + this.alternatesPerPage;
        if (start > this.recentAlternateProducts?.length) {
          this.resetAlternateIndex();
          start = 0;
          end = 2;
        }
        return this.recentAlternateProducts ? this.recentAlternateProducts.slice(start, end) : [];
      });

      this.showAlternatePrev = computed(() => this.alternateSlideIndex > 0);
      this.showAlternateNext = computed(() => this.alternateSlideIndex < this.alternatePages - 1);

      this.showAlternateProductPopUp = true;
    },
    alternateNext() {
      if (this.alternateSlideIndex < this.alternatePages - 1) {
        this.alternateSlideIndex++;
      }
    },
    alternatePrev() {
      if (this.alternateSlideIndex > 0) {
        this.alternateSlideIndex--;
      }
    },
    resetAlternateIndex(){
      this.alternateSlideIndex = 0;
    },
    closeAlternateProductModal() {
      this.showAlternateProductPopUp = false;
      this.alternateSlideIndex = 0;
    }
  }
};
</script>
<style land="css">
.product-variants .overlay > div {
  overflow-y: auto;
  max-height: 500px;
  min-width: 800px;
  padding: 2rem !important;
}
</style>

<style lang="scss" scoped>
.product-variants {
  // Width of each table column
  $table-columns: (
    1: 10%,
    2: 80%,
    3: 5%,
    4: 5%
  ) !default;

  table {
    table-layout: auto;
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
    text-align: left;
    margin-bottom: 20px;

    @include min-screen(grid-breakpoint(md)) {
      table-layout: auto;
    }

    // Generate classes for each column and assign width
    @each $table-column-key, $table-column-value in $table-columns {
      .table-col-#{$table-column-key} {
        width: $table-column-value;
      }
    }

    > thead > tr {
      background: $teal-lighter;
      > td,
      > th {
        font-size: 16px;
        font-weight: 900;
        font-style: normal;
        font-stretch: normal;
        line-height: normal;
        letter-spacing: normal;
        padding: 15px 5px 15px 15px;
        text-align: left;
      }
    }

    > tbody {
      background: $white;

      > tr:nth-child(2n) {
        background: $grayish-cyan-light;
      }

      > tr {
        td:first-child {
          color: $teal-darkest;
        }
        > td,
        > th {
          font-size: 16px;
          font-weight: normal;
          font-style: normal;
          font-stretch: normal;
          line-height: normal;
          letter-spacing: normal;
          color: $gray-darkest;
          padding: 15px 5px 15px 15px;
        }
      }
      .favorite-icon {
        fill: $cyan-dark;
        margin: 0 5px;
        cursor: pointer;
      }
    }
  }
  &__center {
    text-align: center !important;
    .material-design-icon {
      display: inline-block;
    }
  }
  &__order {
    float: right;
  }
  &__imgWrapper {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-right: 8px;
    text-align: left;
    img {
      max-height: 50px;
      max-width: 50px;
      margin: auto;
      -ms-interpolation-mode: bicubic;
    }
  }
  .is-insured {
    margin-right: 10px;
    fill: $greenish;
  }
  .not-insured {
    margin-right: 10px;
    fill: $red;
  }
  .clickable {
    cursor: pointer;
  }
}
</style>
